/**
 * Calibration-runs API surface.
 *
 * Dispatches each call to either the in-memory mock (`mock-api.ts`) or the
 * real backend client (`real-api.ts`) based on `isUsingMockApi()`. The flag is
 * checked per-call so it can be flipped at runtime via the localStorage
 * override without a reload. Identity helpers (`getBotId`/`getUserId`/
 * `getChannel`/`buildIdentityHeaders`/`throwIfNotOk`) aren't dispatched — they
 * read local/shared state rather than the network, so mock mode doesn't
 * change their behavior — and are re-exported unchanged for
 * `metrics-api.ts`/`environments-api.ts`, which import them from here.
 *
 * Real API is the default; set the flag to swap in mocks for offline / local
 * dev where the backend is unavailable (e.g. `VITE_BACKEND_URL` unset) — see
 * `.env.example`.
 */

export {
  buildIdentityHeaders,
  getBotId,
  getChannel,
  getUserId,
  throwIfNotOk,
} from './real-api';

import {
  mockCancelCalibrationRun,
  mockCreateCalibrationRun,
  mockDeleteCalibrationRun,
  mockGetCalibrationRun,
  mockGetConversationCount,
  mockGetLiveConversationCount,
  mockGetPresignedReportDownloadUrl,
  mockGetRunEventStatus,
  mockListCalibrationRuns,
  mockResolveUsers,
} from '../mocks/mock-api';
import {
  type GetConversationCountArgs,
  type GetLiveConversationCountArgs,
  type RunEventStatus,
  cancelCalibrationRun as realCancelCalibrationRun,
  createCalibrationRun as realCreateCalibrationRun,
  deleteCalibrationRun as realDeleteCalibrationRun,
  getCalibrationRun as realGetCalibrationRun,
  getConversationCount as realGetConversationCount,
  getLiveConversationCount as realGetLiveConversationCount,
  getPresignedReportDownloadUrl as realGetPresignedReportDownloadUrl,
  getRunEventStatus as realGetRunEventStatus,
  listCalibrationRuns as realListCalibrationRuns,
  resolveUsers as realResolveUsers,
} from './real-api';

export type { GetConversationCountArgs, GetLiveConversationCountArgs, RunEventStatus };

const LS_KEY = 'calibration.useMock';

/**
 * Whether calibration-runs calls should use the in-memory mock.
 *
 * Resolution order:
 *   1. `localStorage["calibration.useMock"]` (`"true"` / `"false"`) — runtime override.
 *   2. `import.meta.env.VITE_USE_MOCK_CALIBRATION === "true"` — build-time flag.
 *   3. Default: `false` (use the real API).
 */
export function isUsingMockApi(): boolean {
  if (typeof window !== 'undefined') {
    const ls = window.localStorage.getItem(LS_KEY);
    if (ls === 'true') return true;
    if (ls === 'false') return false;
  }
  return import.meta.env.VITE_USE_MOCK_CALIBRATION === 'true';
}

/** Toggle the runtime override; pass `null` to clear and fall back to the env flag. */
export function setUseMockApi(value: boolean | null): void {
  if (typeof window === 'undefined') return;
  if (value === null) {
    window.localStorage.removeItem(LS_KEY);
  } else {
    window.localStorage.setItem(LS_KEY, value ? 'true' : 'false');
  }
}

export const listCalibrationRuns: typeof realListCalibrationRuns = (...args) =>
  (isUsingMockApi() ? mockListCalibrationRuns : realListCalibrationRuns)(...args);

export const getCalibrationRun: typeof realGetCalibrationRun = (...args) =>
  (isUsingMockApi() ? mockGetCalibrationRun : realGetCalibrationRun)(...args);

export const createCalibrationRun: typeof realCreateCalibrationRun = (...args) =>
  (isUsingMockApi() ? mockCreateCalibrationRun : realCreateCalibrationRun)(...args);

export const deleteCalibrationRun: typeof realDeleteCalibrationRun = (...args) =>
  (isUsingMockApi() ? mockDeleteCalibrationRun : realDeleteCalibrationRun)(...args);

export const cancelCalibrationRun: typeof realCancelCalibrationRun = (...args) =>
  (isUsingMockApi() ? mockCancelCalibrationRun : realCancelCalibrationRun)(...args);

export const getRunEventStatus: typeof realGetRunEventStatus = (...args) =>
  (isUsingMockApi() ? mockGetRunEventStatus : realGetRunEventStatus)(...args);

export const getConversationCount: typeof realGetConversationCount = (...args) =>
  (isUsingMockApi() ? mockGetConversationCount : realGetConversationCount)(...args);

export const getLiveConversationCount: typeof realGetLiveConversationCount = (...args) =>
  (isUsingMockApi() ? mockGetLiveConversationCount : realGetLiveConversationCount)(...args);

export const resolveUsers: typeof realResolveUsers = (...args) =>
  (isUsingMockApi() ? mockResolveUsers : realResolveUsers)(...args);

export const getPresignedReportDownloadUrl: typeof realGetPresignedReportDownloadUrl = (...args) =>
  (isUsingMockApi() ? mockGetPresignedReportDownloadUrl : realGetPresignedReportDownloadUrl)(
    ...args
  );
